import React, { useState, useMemo } from 'react';
import { Badge } from '../../../../../components/badge';
import { Clock, Search, X } from 'lucide-react';
import type { SurveySummaryQuestion } from '../../types/survey.types';
import { formatDateTimeLabel, normalizeTimeForComparison, formatTimeForDisplay } from '../../../../../lib/date-time';
import { TimeFilterPicker } from '../../../../../components/time-filter-picker';

interface ViewAllResponsesModalProps {
    isOpen: boolean;
    onClose: () => void;
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const ViewAllResponsesModal: React.FC<ViewAllResponsesModalProps> = ({
    isOpen,
    onClose,
    question,
    totalResponses
}) => {
    const { position, title, type, answered_count, options } = question;
    const [generalFilter, setGeneralFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';

    // Use the helper function for formatting response labels

    // Get appropriate filter placeholder based on question type
    const getFilterPlaceholder = (questionType: string) => {
        switch (questionType) {
            case 'time':
                return 'Filter by time (e.g., 09:30, 14:15)';
            case 'date':
                return 'Filter by date (e.g., 12/31/2023)';
            case 'datetime':
                return 'Filter by date/time (e.g., 12/31/2023 09:30)';
            case 'email':
                return 'Filter by email (e.g., user@example.com)';
            case 'phone':
                return 'Filter by phone (e.g., +1234567890)';
            case 'short_answer':
            case 'long_answer':
                return 'Filter by text content...';
            case 'number':
                return 'Filter by number (e.g., 123, 45.67)';
            case 'rating':
            case 'scale':
                return 'Filter by rating (e.g., 5, 4.5)';
            case 'multiple_choice':
            case 'dropdown':
            case 'checkboxes':
                return 'Filter by option text...';
            default:
                return 'Filter responses...';
        }
    };

    // Get appropriate filter icon based on question type
    const getFilterIcon = (questionType: string) => {
        switch (questionType) {
            case 'time':
            case 'datetime':
                return <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />;
            case 'email':
                return <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />;
            case 'phone':
                return <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />;
            case 'number':
            case 'rating':
            case 'scale':
                return <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />;
            default:
                return <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />;
        }
    };

    // Get appropriate icon based on question type
    const getIcon = (questionType: string) => {
        switch (questionType) {
            case 'date':
                return (
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.2666 1.68677V5.0201" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9326 1.68677V5.0201" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16.4329 3.35327H4.76628C3.8458 3.35327 3.09961 4.09946 3.09961 5.01994V16.6866C3.09961 17.6071 3.8458 18.3533 4.76628 18.3533H16.4329C17.3534 18.3533 18.0996 17.6071 18.0996 16.6866V5.01994C18.0996 4.09946 17.3534 3.35327 16.4329 3.35327Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.09961 8.35327H18.0996" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case 'time':
                return (
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5999 18.5834C15.2023 18.5834 18.9333 14.8525 18.9333 10.2501C18.9333 5.64771 15.2023 1.91675 10.5999 1.91675C5.99756 1.91675 2.2666 5.64771 2.2666 10.2501C2.2666 14.8525 5.99756 18.5834 10.5999 18.5834Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.5999 5.2501V10.2501L13.5999 12.2501" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            case 'datetime':
                return (
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.0996 6.6696V5.4196C18.0996 4.97757 17.924 4.55365 17.6115 4.24108C17.2989 3.92852 16.875 3.75293 16.4329 3.75293H4.76628C4.32425 3.75293 3.90033 3.92852 3.58776 4.24108C3.2752 4.55365 3.09961 4.97757 3.09961 5.4196V17.0863C3.09961 17.5283 3.2752 17.9522 3.58776 18.2648C3.90033 18.5773 4.32425 18.7529 4.76628 18.7529H7.68294" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9326 2.08594V5.41927" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7.2666 2.08594V5.41927" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.09961 8.75293H7.26628" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15.1826 15.0026L13.9326 14.0026V12.0859" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9326 18.7529C16.694 18.7529 18.9326 16.5144 18.9326 13.7529C18.9326 10.9915 16.694 8.75293 13.9326 8.75293C11.1712 8.75293 8.93262 10.9915 8.93262 13.7529C8.93262 16.5144 11.1712 18.7529 13.9326 18.7529Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
            default:
                return (
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.0993 2.51672H5.59928C5.15726 2.51672 4.73333 2.69232 4.42077 3.00488C4.10821 3.31744 3.93262 3.74136 3.93262 4.18339V17.5167C3.93262 17.9588 4.10821 18.3827 4.42077 18.6952C4.73333 19.0078 5.15726 19.1834 5.59928 19.1834H15.5993C16.0413 19.1834 16.4652 19.0078 16.7778 18.6952C17.0904 18.3827 17.266 17.9588 17.266 17.5167V6.68339L13.0993 2.51672Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.2666 2.51672V5.85006C12.2666 6.29208 12.4422 6.71601 12.7548 7.02857C13.0673 7.34113 13.4912 7.51672 13.9333 7.51672H17.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.93327 8.3501H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9333 11.6835H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.9333 15.0167H7.2666" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                );
        }
    };

    // Filter responses based on general filter
    const filteredOptions = useMemo(() => {
        if (!generalFilter) return options;
        
        return options.filter(option => {
            const formattedLabel = formatDateTimeLabel(option.label, type);
            
            // Special handling for time type with time picker
            if (type === 'time') {
                // Normalize both the response time and filter time for comparison
                const normalizedResponseTime = normalizeTimeForComparison(formattedLabel);
                const normalizedFilterTime = normalizeTimeForComparison(generalFilter);
                
                // Direct comparison of normalized times
                if (normalizedResponseTime && normalizedFilterTime) {
                    return normalizedResponseTime === normalizedFilterTime;
                }
                
                // Fallback to text-based search for partial matches
                return formattedLabel.toLowerCase().includes(generalFilter.toLowerCase());
            }
            
            // Default text-based filtering for other types
            return formattedLabel.toLowerCase().includes(generalFilter.toLowerCase());
        });
    }, [options, generalFilter, type]);

    // Paginate filtered options
    const paginatedOptions = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredOptions.slice(startIndex, endIndex);
    }, [filteredOptions, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredOptions.length / itemsPerPage);

    // Reset page when filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [generalFilter]);

    if (!isOpen) return null;

    return (
        <div className="fixed z-50 flex items-center justify-center bg-black/80" style={{
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            minHeight: '100vh',
            position: 'fixed'
        }}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col relative">
                {/* Close Button */}
                <button 
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 z-10" 
                    onClick={onClose}
                >
                    <X className="w-6 h-6" />
                </button>
                
                <div className="flex-shrink-0 pb-4 px-6 pt-6">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#F2FCFA] dark:bg-emerald-900/30">
                            {getIcon(type)}
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {position}. {title}
                            </h2>
                            <div className="flex items-center gap-4 mt-2">
                                <Badge 
                                    variant="outline" 
                                    className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                                >
                                    {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </Badge>
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                                </div>
                                <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]">
                                    {responseRate}% response rate
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="border-b border-gray-200 dark:border-gray-600 -mx-6">                    </div>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col px-6">
                    {/* General Filter Section */}
                    <div className="mb-4 flex-shrink-0">
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                            Filter Responses
                        </label>
                        <div className="relative">
                            {type === 'time' ? (
                                <TimeFilterPicker
                                    value={generalFilter}
                                    onChange={(time) => setGeneralFilter(time)}
                                />
                            ) : (
                                <div className="relative">
                                    {getFilterIcon(type)}
                                    <input
                                        type="text"
                                        value={generalFilter}
                                        onChange={(e) => setGeneralFilter(e.target.value)}
                                        placeholder={getFilterPlaceholder(type)}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 focus:border-gray-300 dark:focus:border-gray-500"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Response Count Info */}
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-4 flex-shrink-0 text-right">
                        {generalFilter ? (
                            <>Showing {filteredOptions.length} of {options.length} responses</>
                        ) : (
                            <>Showing all {filteredOptions.length} responses</>
                        )}
                    </div>

                    {/* Response List */}
                    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-gray-800">
                        <div className="space-y-2 mb-6">
                            {paginatedOptions.map((option, index) => (
                                <div 
                                    key={option.option_id} 
                                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <p className="text-sm text-gray-900 dark:text-gray-100">
                                        {formatDateTimeLabel(option.label, type)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {paginatedOptions.length === 0 && (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                {generalFilter ? (
                                    <>No responses found matching "{type === 'time' ? formatTimeForDisplay(generalFilter) : generalFilter}"</>
                                ) : (
                                    <>No responses found</>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-4 flex-shrink-0">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewAllResponsesModal;
